import { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Grid3x3, MapPin, CalendarClock, AlertTriangle, HeartPulse, GraduationCap, Inbox,
  Plug, ScrollText, Rocket, User, Calendar, Clock, HelpCircle, FileText, LogOut, LifeBuoy,
  Bell, WifiOff, ShieldCheck, ChevronDown, Building2, Settings, ClipboardList, Boxes, Repeat,
  ClipboardCheck, Megaphone, Newspaper, DoorOpen, PenSquare, Menu, Briefcase, MessagesSquare,
  BookOpen, BarChart3, Wand2, ShieldQuestion, CalendarCheck, ListTodo, Mic, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { api, API } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import AssistantWidget from "@/components/AssistantWidget";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import PwaActions from "@/components/PwaActions";
import { notify } from "@/lib/pwa";

const ROLE_LABELS = {
  master_admin: "Master Admin", owner: "Ägare / Administratör", sysadmin: "Systemadministratör",
  hr_admin: "HR & Arbetsmiljö", chef: "Chef", teamledare: "Teamledare", medarbetare: "Medarbetare",
};

const ROUTE_MODULE = {
  "/schema": "schema", "/skiftmonster": "schema", "/bemanningsbehov": "schema", "/stationer": "schema",
  "/rotation": "rotation", "/internrekrytering": "internrekrytering", "/enkater": "enkater",
  "/flode": "flode", "/utskick": "flode", "/utbildningar": "utbildningar", "/arbetsmiljo": "arbetsmiljo",
  "/uppdrag": "uppdrag", "/statistik": "uppdrag", "/bibliotek": "bibliotek", "/it-support": "itsupport",
  "/placeringar": "placement", "/tillganglighet": "placement",
};
function moduleOk(to, enabled) {
  if (!Array.isArray(enabled)) return true;
  const m = ROUTE_MODULE[to];
  return !m || enabled.includes(m);
}

export function flatNavForUser(user, role) {
  const orgModel = user?.company_org_model;
  const enabled = user?.company_enabled_modules;
  const grants = user?.grants || [];
  const isMaster = role === "master_admin";
  const ok = (n) => {
    if (n.masterOnly) return isMaster;
    if (n.perm) return ["owner", "master_admin"].includes(role) || grants.includes(n.perm);
    if (user?.is_placement_manager && ["/placeringar", "/tillganglighet"].includes(n.to)) return true;
    return ["owner", "master_admin"].includes(role) ? true : (n.roles || []).includes(role);
  };
  return NAV_SECTIONS.flatMap((s) => s.items).filter((n) =>
    ok(n) &&
    (n.to !== "/rotation" || user?.company_rotation_enabled !== false) &&
    moduleOk(n.to, enabled) &&
    (!n.model || !orgModel || n.model.includes(orgModel))
  );
}

export const NAV_SECTIONS = [
  { title: null, items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
    { to: "/min-avdelning", label: "Min avdelning", icon: ClipboardList, roles: ["owner", "hr_admin", "chef", "teamledare"] },
  ] },
  { title: "Mitt arbete", items: [
    { to: "/struktur", label: "Struktur", icon: ListTodo, roles: ["owner", "hr_admin", "chef", "teamledare", "creator", "sysadmin"] },
    { to: "/moten", label: "Möten & inspelning", icon: Mic, roles: ["owner", "hr_admin", "chef", "teamledare", "creator", "sysadmin"] },
  ] },
  { title: "Schema & bemanning", items: [
    { to: "/schema", label: "Schema & faktiskt", icon: CalendarClock, roles: ["owner", "chef", "teamledare"], model: ["skift", "bemanning"] },
    { to: "/skiftmonster", label: "Skiftmönster", icon: Repeat, roles: ["owner", "chef"], model: ["skift"] },
    { to: "/bemanningsbehov", label: "Bemanningsbehov", icon: Boxes, roles: ["owner", "chef"], model: ["skift"] },
    { to: "/stationer", label: "Stationer & belastning", icon: MapPin, roles: ["owner"], model: ["skift"] },
    { to: "/rotation", label: "Rotationsvarningar", icon: AlertTriangle, roles: ["owner", "chef", "teamledare"], model: ["skift"] },
    { to: "/tidigare-hemgang", label: "Tidigare hemgång", icon: DoorOpen, roles: ["owner", "hr_admin", "chef", "teamledare"], model: ["skift"] },
  ] },
  { title: "Personal & kompetens", items: [
    { to: "/medarbetare", label: "Medarbetare", icon: Users, roles: ["owner", "hr_admin"] },
    { to: "/organisation", label: "Organisation", icon: Building2, roles: ["owner", "hr_admin"] },
    { to: "/kompetensmatris", label: "Kompetensmatris", icon: Grid3x3, roles: ["owner", "hr_admin", "chef", "teamledare"] },
    { to: "/cv", label: "CV", icon: FileText, roles: ["owner", "hr_admin", "chef", "teamledare"] },
    { to: "/internrekrytering", label: "Internrekrytering", icon: Briefcase, roles: ["owner", "hr_admin", "chef"] },
    { to: "/utbildningar", label: "Utbildningar", icon: GraduationCap, roles: ["owner", "hr_admin", "chef", "teamledare"] },
    { to: "/arbetsmiljo", label: "Arbetsmiljö", icon: HeartPulse, roles: ["owner", "hr_admin"] },
    { to: "/franvaro", label: "Sjuk & ledighet", icon: HeartPulse, roles: ["owner", "chef"], model: ["skift"] },
  ] },
  { title: "Kommunikation", items: [
    { to: "/flode", label: "Flöde", icon: Newspaper, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
    { to: "/utskick", label: "Skiftutskick", icon: Megaphone, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
    { to: "/enkater", label: "Enkäter", icon: ClipboardCheck, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
    { to: "/uppdrag", label: "Uppdrag", icon: Inbox, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
    { to: "/statistik", label: "Statistik", icon: BarChart3, roles: ["owner", "creator"] },
    { to: "/bibliotek", label: "Policys & rutiner", icon: BookOpen, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
    { to: "/meddelanden", label: "Meddelanden", icon: MessagesSquare, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
  ] },
  { title: "Kund & placering", items: [
    { to: "/placeringar", label: "Kund & placering", icon: Briefcase, roles: ["owner", "hr_admin", "chef"] },
    { to: "/tillganglighet", label: "Tillgänglighet", icon: CalendarCheck, roles: ["owner", "hr_admin", "chef"] },
  ] },
  { title: "System & inställningar", items: [
    { to: "/skraddare", label: "Organisationsskräddare", icon: Wand2, roles: [], perm: "setup:admin" },
    { to: "/rollskraddare", label: "Rollskräddare", icon: Sparkles, roles: ["hr_admin", "chef", "teamledare", "creator", "sysadmin"] },
    { to: "/it-support", label: "IT & Admin", icon: ShieldQuestion, roles: ["owner", "hr_admin", "chef", "teamledare", "creator"] },
    { to: "/support", label: "Support", icon: LifeBuoy, roles: ["owner", "hr_admin", "chef", "teamledare"] },
    { to: "/integrationer", label: "Integrationer", icon: Plug, roles: ["owner", "sysadmin"] },
    { to: "/installningar", label: "Inställningar", icon: Settings, roles: ["owner"] },
    { to: "/audit", label: "Auditlogg", icon: ScrollText, roles: ["owner", "sysadmin"] },
    { to: "/innehall", label: "Text-editerare", icon: PenSquare, roles: [], masterOnly: true },
  ] },
];

const EMP_NAV = [
  { to: "/idag", label: "Idag", icon: Calendar },
  { to: "/emp-schema", label: "Schema", icon: Clock },
  { to: "/flode", label: "Flöde", icon: Newspaper },
  { to: "/tidigare-hemgang", label: "Hemgång", icon: DoorOpen, model: ["skift"] },
  { to: "/struktur", label: "Struktur", icon: ListTodo },
  { to: "/moten", label: "Möten", icon: Mic },
  { to: "/cv", label: "Mitt CV", icon: FileText },
  { to: "/internrekrytering", label: "Jobb", icon: Briefcase },
  { to: "/utbildningar", label: "Utbildningar", icon: GraduationCap },
  { to: "/bibliotek", label: "Policys", icon: BookOpen },
  { to: "/tillganglighet", label: "Tillgänglighet", icon: CalendarCheck },
  { to: "/it-support", label: "IT-hjälp", icon: ShieldQuestion },
  { to: "/meddelanden", label: "Chatt", icon: MessagesSquare },
  { to: "/fragor", label: "Frågor", icon: HelpCircle },
  { to: "/ledighet", label: "Ledighet", icon: FileText },
  { to: "/profil", label: "Profil", icon: User },
];

function ConnBanner() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (online) return null;
  return (
    <div data-testid="offline-banner" className="flex items-center justify-center gap-2 bg-amber-500 px-4 py-1.5 text-sm font-medium text-amber-950">
      <WifiOff className="h-4 w-4" /> Ingen anslutning – informationen kan vara inaktuell och uppdateras inte.
    </div>
  );
}

function Notifications() {
  const [items, setItems] = useState([]);
  const seen = useRef(null);
  const load = () => api.get("/notifications").then((r) => {
    const data = r.data || [];
    if (seen.current === null) {
      seen.current = new Set(data.map((n) => n.id));
    } else {
      data.filter((n) => !n.read && !seen.current.has(n.id)).forEach((n) => notify("Kompetens & Rotation", n.body));
      data.forEach((n) => seen.current.add(n.id));
    }
    setItems(data);
  }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  const nav = useNavigate();
  const openNotif = (n) => { api.post(`/notifications/${n.id}/read`).then(load).catch(() => {}); if (n.link) nav(n.link); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative kr-focus" data-testid="notifications-trigger">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span data-testid="notif-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">{unread}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>Notifieringar</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && <div className="px-2 py-6 text-center text-sm text-muted-foreground">Inga notifieringar</div>}
        {items.slice(0, 12).map((n) => (
          <DropdownMenuItem key={n.id} onClick={() => openNotif(n)} className="items-start gap-2 whitespace-normal" data-testid={`notif-${n.id}`}>
            <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${n.read ? "bg-muted text-muted-foreground" : "bg-accent text-accent-foreground"}`}>
              {(n.meta?.avatar || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "•"}
            </span>
            <div className="min-w-0 flex-1">
              <span className={`block text-sm ${n.read ? "text-muted-foreground" : "font-medium"}`}>{n.body}</span>
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{n.message_type}</span>
            </div>
            {n.meta?.image_path && <img src={`${API}/files/${n.meta.image_path}`} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { user, logout, setViewRole } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/login"); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-2 kr-focus" data-testid="user-menu-trigger">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </span>
          <span className="hidden text-left sm:block">
            <span className="block text-sm font-semibold leading-tight">{user?.name}</span>
            <span className="block text-[11px] text-muted-foreground">{user?.role_label}</span>
          </span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
        <div className="px-2 pb-1 text-[11px] text-muted-foreground" data-testid="access-level">Åtkomstnivå {user?.access_level ?? "–"}/4</div>
        <DropdownMenuSeparator />
        {(user?.role === "owner" || user?.role === "master_admin") && (
          <>
            <DropdownMenuLabel className="flex items-center gap-1.5 text-[11px] font-normal text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> Förhandsvisa som roll (endast vy)
            </DropdownMenuLabel>
            {Object.entries(ROLE_LABELS).map(([r, l]) => (
              <DropdownMenuItem key={r} data-testid={`view-role-${r}`}
                onClick={() => setViewRole(r === "owner" || r === "master_admin" ? null : r)}>
                {l}{((user.view_role || user.role) === r) && <Badge variant="secondary" className="ml-auto">Aktiv</Badge>}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={doLogout} data-testid="logout-btn" className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" /> {t("logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NAV_EN = {
  "/dashboard": "Dashboard", "/min-avdelning": "My department", "/onboarding": "Onboarding",
  "/medarbetare": "Employees", "/organisation": "Organization", "/kompetensmatris": "Competence matrix", "/cv": "CV",
  "/stationer": "Stations & load", "/bemanningsbehov": "Staffing demand", "/skiftmonster": "Shift patterns",
  "/schema": "Schedule & actual", "/rotation": "Rotation alerts", "/enkater": "Surveys",
  "/utskick": "Broadcasts", "/flode": "Feed", "/tidigare-hemgang": "Early leave",
  "/internrekrytering": "Internal recruitment", "/support": "Support", "/meddelanden": "Messages", "/franvaro": "Sick & leave", "/arbetsmiljo": "Work environment",
  "/integrationer": "Integrations", "/installningar": "Settings", "/audit": "Audit log", "/innehall": "Text editor",
  "/utbildningar": "Trainings", "/uppdrag": "Assignments",
  "/bibliotek": "Policies & routines", "/statistik": "Statistics",
  "/skraddare": "Setup wizard", "/it-support": "IT & Admin",
  "/rollskraddare": "Role tailor", "/struktur": "Structure", "/moten": "Meetings",
  "/placeringar": "Clients & placements", "/tillganglighet": "Availability",
  "/idag": "Today", "/emp-schema": "Schedule", "/fragor": "Questions", "/ledighet": "Leave", "/profil": "Profile",
};
function navLabel(lang, n, model) {
  if (n.to === "/utskick" && model && model !== "skift") return lang === "en" ? "Broadcasts" : "Utskick";
  if (n.to === "/medarbetare" && model === "bemanning") return lang === "en" ? "Consultants" : "Konsulter";
  return lang === "en" ? (NAV_EN[n.to] || n.label) : n.label;
}

function useBranding() {
  const { data } = useApi("/content/branding");
  return { name: data?.product_name || "Kompetens", tagline: data?.tagline || "& Rotation" };
}

function LangSwitch() {
  const { lang, setLang } = useLang();
  const { user } = useAuth();
  useEffect(() => {
    if (user && user.lang && user.lang !== lang) setLang(user.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.lang]);
  const avail = (user && user.company_languages) || ["sv", "en"];
  const change = (l) => { setLang(l); api.patch("/auth/me/lang", { lang: l }).catch(() => {}); };
  if ((avail || []).length < 2) return null;
  return (
    <div className="flex items-center overflow-hidden rounded-md border text-xs" data-testid="lang-switch">
      {avail.map((l) => (
        <button key={l} onClick={() => change(l)} data-testid={`lang-${l}`}
          className={`px-2 py-1 font-bold uppercase transition-colors ${lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, effectiveRole } = useAuth();
  const isEmployee = effectiveRole === "medarbetare";
  const supportBadge = useSupportBadge();
  const chatBadge = useChatBadge();
  const { lang } = useLang();
  const isMaster = effectiveRole === "master_admin";
  const brand = useBranding();
  const orgModel = user?.company_org_model;
  const roleOk = (n) => {
    if (n.perm) return ["owner", "master_admin"].includes(effectiveRole) || (user?.grants || []).includes(n.perm);
    if (user?.is_placement_manager && ["/placeringar", "/tillganglighet"].includes(n.to)) return true;
    return ["owner", "master_admin"].includes(effectiveRole) ? true : (n.roles || []).includes(effectiveRole);
  };
  const hidden = [...(user?.company_menu_hidden || []), ...(user?.menu_prefs?.hidden || [])];
  const itemVisible = (n) =>
    (n.masterOnly ? isMaster : roleOk(n)) &&
    (n.to !== "/rotation" || user?.company_rotation_enabled !== false) &&
    moduleOk(n.to, user?.company_enabled_modules) &&
    (!n.model || !orgModel || n.model.includes(orgModel)) &&
    (!hidden.includes(n.to) || ["/installningar", "/dashboard"].includes(n.to));
  const sections = NAV_SECTIONS
    .map((s) => ({ ...s, items: s.items.filter(itemVisible) }))
    .filter((s) => s.items.length);
  const flatNav = sections.flatMap((s) => s.items);
  const order = (user?.menu_prefs?.order?.length ? user.menu_prefs.order : user?.company_menu_order) || [];
  const orderedFlat = order.length
    ? [...flatNav].sort((a, b) => {
        const ia = order.indexOf(a.to), ib = order.indexOf(b.to);
        return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
      })
    : flatNav;
  const pinned = user?.menu_prefs?.pinned || [];
  const QUICK = [...new Set([...pinned, "/dashboard", "/flode", "/placeringar", "/schema", "/min-avdelning", "/meddelanden"])];
  let quick = QUICK.map((to) => orderedFlat.find((n) => n.to === to)).filter(Boolean);
  for (const n of orderedFlat) { if (quick.length >= 3) break; if (!quick.includes(n)) quick.push(n); }
  quick = quick.slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <ConnBanner />
      <AssistantWidget />
      {isEmployee ? (
        <EmployeeLayout>{children}</EmployeeLayout>
      ) : (
        <div className="flex min-h-screen">
          <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r bg-primary text-primary-foreground lg:flex">
            <div className="flex items-center gap-2.5 px-5 py-5">
              <img src={user?.company_logo || "/brand-logo.png"} alt="" className="h-9 w-9 rounded-lg object-contain" />
              <div className="leading-tight">
                <div className="font-head text-base font-bold">{user?.company_name || brand.name}</div>
                <div className="text-[11px] text-blue-200">{brand.tagline}</div>
              </div>
            </div>
            <div className="mx-4 mb-3 flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs">
              <Building2 className="h-4 w-4 shrink-0" />
              <span className="truncate" data-testid="company-name">{user?.company_name || "Ingen organisation"}</span>
            </div>
            <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-6 kr-scroll">
              {(order.length ? [{ title: null, items: orderedFlat }] : sections).map((s, si) => (
                <div key={si} className="space-y-1">
                  {s.title && <div className="px-3 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-blue-300/80">{s.title}</div>}
                  {s.items.map((n) => {
                    const Icon = n.icon;
                    return (
                      <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                            isActive ? "bg-white/15 text-white" : "text-blue-100 hover:bg-white/10"}`}>
                        <Icon className="h-[18px] w-[18px]" /> <span className="flex-1">{navLabel(lang, n, orgModel)}</span>
                        {n.to === "/support" && supportBadge > 0 &&
                          <span className="rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white" data-testid="support-badge">{supportBadge}</span>}
                        {n.to === "/meddelanden" && chatBadge > 0 &&
                          <span className="rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white" data-testid="chat-badge">{chatBadge}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              ))}
            </nav>
          </aside>
          <div className="flex min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-30 flex items-center gap-2 border-b bg-card/90 px-4 py-2.5 backdrop-blur">
              <div className="flex items-center gap-2 lg:hidden">
                <img src={user?.company_logo || "/brand-logo.png"} alt="" className="h-7 w-7 rounded-md object-contain" />
                <span className="max-w-[150px] truncate font-head text-sm font-bold">{user?.company_name || brand.name}</span>
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <LangSwitch />
                <PwaActions />
                <Notifications />
                <UserMenu />
              </div>
            </header>
            <main className="min-w-0 flex-1 p-4 pb-24 sm:p-6 lg:pb-6">{children}</main>
          </div>
          <MobileNav nav={orderedFlat} quick={quick} badge={supportBadge} orgModel={orgModel} />
        </div>
      )}
    </div>
  );
}

function useSupportBadge() {
  const [count, setCount] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    api.get("/support/badge").then((r) => alive && setCount(r.data.count || 0)).catch(() => {});
    return () => { alive = false; };
  }, [loc.pathname]);
  return count;
}

function useChatBadge() {
  const [count, setCount] = useState(0);
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/chat/unread").then((r) => alive && setCount(r.data.total || 0)).catch(() => {});
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, [loc.pathname]);
  return count;
}

function MobileNav({ nav, quick, badge, orgModel }) {
  const [open, setOpen] = useState(false);
  const { lang } = useLang();
  return (
    <>
      <nav className="fixed bottom-0 left-0 z-30 flex w-full border-t bg-card shadow-[0_-2px_10px_rgba(0,0,0,0.06)] lg:hidden" data-testid="mobile-bottom-nav">
        {quick.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} data-testid={`mnav-${n.to.slice(1)}`}
              className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium ${isActive ? "text-accent" : "text-muted-foreground"}`}>
              <Icon className="h-5 w-5" /><span className="max-w-full truncate px-1">{navLabel(lang, n, orgModel)}</span>
            </NavLink>
          );
        })}
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-trigger"
          className="relative flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium text-muted-foreground">
          <Menu className="h-5 w-5" /><span>Meny</span>
          {badge > 0 && <span className="absolute right-3 top-1.5 rounded-full bg-red-600 px-1 text-[9px] font-bold text-white">{badge}</span>}
        </button>
      </nav>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 lg:hidden" onClick={() => setOpen(false)} data-testid="mobile-menu-overlay">
          <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-primary p-4 text-primary-foreground kr-scroll" onClick={(e) => e.stopPropagation()} data-testid="mobile-menu-sheet">
            <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-white/30" />
            <div className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-blue-200">Meny</div>
            <div className="grid grid-cols-2 gap-1.5">
              {nav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mmenu-${n.to.slice(1)}`}
                    className={({ isActive }) => `flex items-center gap-2 rounded-xl px-3 py-3 text-sm ${isActive ? "bg-white/15" : "hover:bg-white/10"}`}>
                    <Icon className="h-[18px] w-[18px] shrink-0" /> <span className="flex-1 truncate">{navLabel(lang, n, orgModel)}</span>
                    {n.to === "/support" && badge > 0 && <span className="rounded-full bg-red-600 px-1.5 text-[10px] font-bold text-white">{badge}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EmployeeLayout({ children }) {
  const { user } = useAuth();
  const { lang } = useLang();
  const brand = useBranding();
  const orgModel = user?.company_org_model;
  return (
    <div className="mx-auto min-h-screen max-w-lg bg-slate-50 pb-20">
      <header className="sticky top-0 z-30 flex items-center gap-2 border-b bg-primary px-4 py-3 text-primary-foreground">
        <img src={user?.company_logo || "/brand-logo.png"} alt="" className="h-8 w-8 rounded-md object-contain" />
        <span className="font-head text-sm font-bold">{user?.company_name || `${brand.name} ${brand.tagline}`}</span>
        <div className="ml-auto flex items-center gap-1"><LangSwitch /><PwaActions /><Notifications /><UserMenu /></div>
      </header>
      <main className="p-4">{children}</main>
      <nav className="fixed bottom-0 left-1/2 z-30 flex w-full max-w-lg -translate-x-1/2 border-t bg-card">
        {EMP_NAV.filter((n) => moduleOk(n.to, user?.company_enabled_modules))
          .filter((n) => !n.model || !user?.company_org_model || n.model.includes(user?.company_org_model)).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} data-testid={`emp-nav-${n.to.slice(1)}`}
              className={({ isActive }) => `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium ${isActive ? "text-accent" : "text-muted-foreground"}`}>
              <Icon className="h-5 w-5" /> {navLabel(lang, n, orgModel)}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
